import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLazyQuery } from '@apollo/client'

import { GET_JOB_LISTINGS } from 'src/graphql/joblistings.graphql'
import { toast } from 'src/components/Overlays/Toast/Toast'

import { FilterOption } from '../types'

interface UseJobListingsFilterProps {
  selectedJobListings: string[]
  onJobListingsChange: (jobListings: string[]) => void
}

// How many listings to load per page. The menu scroll container shows ~14 rows
// at a time; 100 per page gives the user enough to scroll through before the
// next page loads.
const PAGE_SIZE = 100

const SEARCH_DEBOUNCE_MS = 250

type JobListingItem = { title: string; jobListingId: string }
type JobListingsData = {
  jobListings?: { items?: JobListingItem[] | null } | null
}

const extractItems = (
  data: JobListingsData | undefined | null
): FilterOption[] =>
  data?.jobListings?.items?.map((jobListing) => ({
    label: jobListing.title,
    value: jobListing.jobListingId,
  })) || []

export const useJobListingsFilter = ({
  selectedJobListings,
  onJobListingsChange,
}: UseJobListingsFilterProps) => {
  const [options, setOptions] = useState<FilterOption[]>([])
  const [isLoaded, setIsLoaded] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  // Pagination state lives in refs so loadMore reads the current offset and
  // search synchronously when firing the next page.
  const offsetRef = useRef(0)
  const currentSearchRef = useRef('')

  // searchVersion bumps every time a new search (or reset) starts. A load-more
  // request captures the version at fire time; its response only applies if
  // the version still matches, so a slow load-more for an old search can't
  // append stale rows onto a newer search's results.
  const searchVersionRef = useRef(0)

  // Synchronous in-flight guards. React state (loadingMore) updates async,
  // so the IntersectionObserver can fire onLoadMore multiple times within a
  // single tick before the next render and slip past a useState-only guard,
  // producing duplicate offsets. A ref read is synchronous and catches them.
  const pendingResetRef = useRef(false)
  const pendingAppendRef = useRef(false)

  // Pending search keystrokes accumulate in pendingSearchRef; a debounce timer
  // fires the actual query when typing pauses.
  const pendingSearchRef = useRef('')
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onErrorToast = useCallback(() => {
    toast('Failed to load job listings', 'error')
  }, [])

  const [loadJobListings, { loading }] = useLazyQuery(GET_JOB_LISTINGS, {
    fetchPolicy: 'network-only',
  })

  const fireReset = useCallback(
    (search: string) => {
      const version = ++searchVersionRef.current
      pendingResetRef.current = true
      // Cancel any in-flight append guard; the version bump above causes its
      // response to be dropped if it lands later, and we want a brand-new
      // loadMore (against whatever search context survives this reset) to be
      // allowed once it completes.
      pendingAppendRef.current = false
      // Refs (currentSearchRef, offsetRef) and state (hasMore) are NOT
      // mutated upfront. The pendingResetRef above is the only thing that
      // blocks loadMore from firing during the reset window — see loadMore.
      // On error we leave everything pointing at the prior search, so the
      // user can continue scrolling the visible results.
      const trimmed = search.trim()
      loadJobListings({
        variables: {
          pageInfo: { offset: 0, limit: PAGE_SIZE },
          ...(trimmed && { filters: { search: trimmed } }),
        },
        onCompleted: (data) => {
          pendingResetRef.current = false
          if (version !== searchVersionRef.current) return
          const items = extractItems(data)
          setOptions(items)
          setIsLoaded(true)
          setHasMore(items.length === PAGE_SIZE)
          // Commit the new search context only on success, so refs and the
          // visible options stay in sync. If we mutated upfront, an error
          // would leave currentSearchRef pointing at the new search while
          // options still showed the old one — next loadMore would append
          // mismatched rows.
          currentSearchRef.current = search
          offsetRef.current = items.length
          setLoadingMore(false)
        },
        onError: () => {
          pendingResetRef.current = false
          onErrorToast()
        },
      })
    },
    [loadJobListings, onErrorToast]
  )

  // Initial load — fires page 1 with empty search the first time the menu
  // opens. The `!loading` guard is critical: the menu calls loadData() from
  // render every pass where isLoaded is false, so without this guard each
  // re-render before the response lands would re-fire fireReset and bump
  // searchVersionRef — invalidating the in-flight response when it arrives.
  const loadData = useCallback(() => {
    if (!isLoaded && !loading) {
      fireReset('')
    }
  }, [isLoaded, loading, fireReset])

  const onSearchChange = useCallback(
    (text: string) => {
      pendingSearchRef.current = text
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      debounceTimerRef.current = setTimeout(() => {
        fireReset(pendingSearchRef.current)
      }, SEARCH_DEBOUNCE_MS)
    },
    [fireReset]
  )

  // Synchronous reset to the initial empty-search page. Used by the menu's
  // submenu-exit cleanup to drop the previous query's narrowed options
  // without the 250ms debounce window during which a re-open would still
  // show stale results.
  const resetToInitial = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    fireReset('')
  }, [fireReset])

  const loadMore = useCallback(() => {
    if (!hasMore) return
    // Ref-based guards beat React state for the IO-fired path: the observer
    // can call this twice synchronously before a re-render reflects
    // loadingMore=true, but the ref flips immediately.
    if (pendingAppendRef.current || pendingResetRef.current) return
    const version = searchVersionRef.current
    const offset = offsetRef.current
    const search = currentSearchRef.current.trim()
    pendingAppendRef.current = true
    setLoadingMore(true)
    loadJobListings({
      variables: {
        pageInfo: { offset, limit: PAGE_SIZE },
        ...(search && { filters: { search } }),
      },
      onCompleted: (data) => {
        pendingAppendRef.current = false
        setLoadingMore(false)
        // If a reset has fired while this page was in flight, the response
        // belongs to a stale search — drop it.
        if (version !== searchVersionRef.current) return
        const items = extractItems(data)
        setOptions((prev) => [...prev, ...items])
        setHasMore(items.length === PAGE_SIZE)
        offsetRef.current = offset + items.length
      },
      onError: () => {
        pendingAppendRef.current = false
        setLoadingMore(false)
        onErrorToast()
      },
    })
  }, [hasMore, loadJobListings, onErrorToast])

  // Cancel any in-flight debounce on unmount so we don't fire a query against
  // a torn-down component.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // Reflect selected state on the options the menu currently sees. Listings
  // that are selected but not in the current search results are intentionally
  // not synthesized here — useConditionalDataLoading hydrates them at page
  // load and saved-filter restoration so their pill labels resolve.
  const optionsWithSelection = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        selected: selectedJobListings.includes(option.value),
      })),
    [options, selectedJobListings]
  )

  return {
    options: optionsWithSelection,
    selected: selectedJobListings,
    onChange: onJobListingsChange,
    loadData,
    isLoaded,
    loading,
    onSearchChange,
    resetToInitial,
    loadMore,
    hasMore,
    loadingMore,
  }
}
